import { ColumnDef } from '@tanstack/react-table';
import { ArrowUpDown, Users } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import type { LiveStreamTableRow, ViewerCountsByEvent } from '@shared/types';
import { ClientNameCell, EventIDCell, EventLinksCell, EventTitleCell, EncoderIDCell } from './cells';
import { formatDateTimeWithClientTZ } from '@/client/lib/date';

// Helper function to render status badge with color coding
export function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <>-</>;

  const normalizedStatus = status.toLowerCase();

  let bgColor = 'bg-gray-100';
  let textColor = 'text-gray-800';

  if (normalizedStatus === 'live') {
    bgColor = 'bg-red-600';
    textColor = 'text-white';
  } else if (normalizedStatus === 'on break') {
    bgColor = 'bg-yellow-500';
    textColor = 'text-white';
  } else if (normalizedStatus === 'starting' || normalizedStatus === 'stopping') {
    bgColor = 'bg-blue-600';
    textColor = 'text-white';
  } else if (normalizedStatus === 'disconnected') {
    bgColor = 'bg-orange-500';
    textColor = 'text-white';
  } else if (normalizedStatus === 'available') {
    bgColor = 'bg-gray-600';
    textColor = 'text-white';
  }

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono font-medium ${bgColor} ${textColor}`}>
      {status}
    </span>
  );
}


// Viewer count badge component
export function ViewerCountBadge({
  count,
  loading,
  available,
}: {
  count: number | undefined;
  loading: boolean;
  available: boolean;
}) {
  if (!available) {
    return <span className="text-xs text-muted-foreground">-</span>;
  }

  if (loading && count === undefined) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <span className="animate-pulse">...</span>
      </span>
    );
  }

  if (count === undefined || count === 0) {
    return <span className="text-xs text-muted-foreground">0</span>;
  }

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono font-medium bg-green-600 text-white">
      <Users className="h-3 w-3" />
      {count.toLocaleString()}
    </span>
  );
}

/**
 * Create live streams columns with viewer counts
 */
export function createLiveStreamsColumns(
  viewerCounts: ViewerCountsByEvent,
  viewerCountsLoading: boolean,
  viewerCountsAvailable: boolean
): ColumnDef<LiveStreamTableRow>[] {
  return [
    {
      accessorKey: 'clientName',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Client
            <ArrowUpDown className="ml-1 h-3 w-3" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const { clientID, clientName } = row.original;
        if (!clientName || !clientID) return <span className="text-xs text-muted-foreground">-</span>;
        return <ClientNameCell clientID={clientID} clientName={clientName} />;
      },
    },
    {
      accessorKey: 'eventTitle',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Event Title
            <ArrowUpDown className="ml-1 h-3 w-3" />
          </Button>
        );
      },
      cell: ({ row }) => <EventTitleCell title={row.getValue('eventTitle')} />,
    },
    {
      accessorKey: 'eventID',
      size: 80,
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Event ID
            <ArrowUpDown className="ml-1 h-3 w-3" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <EventIDCell eventID={row.getValue('eventID')} />
      ),
    },
    {
      accessorKey: 'encoderID',
      size: 80,
      header: () => <span className="text-xs font-medium">Encoder</span>,
      cell: ({ row }) => {
        const encoderID = row.original.encoderID;
        if (!encoderID) return <span className="text-xs text-muted-foreground">-</span>;
        return <EncoderIDCell encoderID={encoderID} />;
      },
    },
    {
      id: 'viewers',
      header: () => (
        <span className="text-xs font-medium">Viewers</span>
      ),
      cell: ({ row }) => {
        const eventID = row.original.eventID;
        // Handle both "clientID_eventID" and plain "eventID" formats
        const eventIDPart = eventID?.includes('_')
          ? eventID.split('_')[1]
          : eventID;

        const count = eventIDPart ? viewerCounts[eventIDPart]?.activePlayers : undefined;
        return (
          <ViewerCountBadge
            count={count}
            loading={viewerCountsLoading}
            available={viewerCountsAvailable}
          />
        );
      },
    },
    {
      accessorKey: 'startDateTime',
      size: 200,
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Started At
            <ArrowUpDown className="ml-1 h-3 w-3" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const dateStr = row.getValue('startDateTime') as string | null;
        const clientTimeZone = row.original.clientTimeZone;
        if (!dateStr) return <span className="text-xs text-muted-foreground">-</span>;
        return <span className="font-mono text-xs whitespace-nowrap">{formatDateTimeWithClientTZ(dateStr, clientTimeZone)}</span>;
      },
    },
    {
      accessorKey: 'status',
      size: 80,
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Status
            <ArrowUpDown className="ml-1 h-3 w-3" />
          </Button>
        );
      },
      cell: ({ row }) => <StatusBadge status={row.getValue('status')}  />,
    },
    {
      id: 'eventLinks',
      header: 'Links',
      cell: ({ row }) => (
        <EventLinksCell
          eventID={row.original.eventID}
          clientID={row.original.clientID}
          clientName={row.original.clientName ?? undefined}
        />
      ),
      size: 70,
      enableSorting: false,
    },
  ];
}

// Legacy static columns (without viewer counts)
export const liveStreamsColumns: ColumnDef<LiveStreamTableRow>[] = [
  {
    accessorKey: 'clientName',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Client
          <ArrowUpDown className="ml-1 h-3 w-3" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const { clientID, clientName } = row.original;
      if (!clientName || !clientID) return <span className="text-xs text-muted-foreground">-</span>;
      return <ClientNameCell clientID={clientID} clientName={clientName} />;
    },
  },
  {
    accessorKey: 'eventTitle',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Event Title
          <ArrowUpDown className="ml-1 h-3 w-3" />
        </Button>
      );
    },
    cell: ({ row }) => <EventTitleCell title={row.getValue('eventTitle')} />,
  },
  {
    accessorKey: 'eventID',
    size: 80,
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Event ID
          <ArrowUpDown className="ml-1 h-3 w-3" />
        </Button>
      );
    },
    cell: ({ row }) => (
      <EventIDCell eventID={row.getValue('eventID')} />
    ),
  },
  {
    accessorKey: 'encoderID',
    size: 80,
    header: () => <span className="text-xs font-medium">Encoder</span>,
    cell: ({ row }) => {
      const encoderID = row.original.encoderID;
      if (!encoderID) return <span className="text-xs text-muted-foreground">-</span>;
      return <EncoderIDCell encoderID={encoderID} />;
    },
  },
  {
    accessorKey: 'startDateTime',
    size: 200,
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Started At
          <ArrowUpDown className="ml-1 h-3 w-3" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const dateStr = row.getValue('startDateTime') as string | null;
      const clientTimeZone = row.original.clientTimeZone;
      if (!dateStr) return <span className="text-xs text-muted-foreground">-</span>;
      return <span className="font-mono text-xs whitespace-nowrap">{formatDateTimeWithClientTZ(dateStr, clientTimeZone)}</span>;
    },
  },
  {
    accessorKey: 'status',
    size: 80,
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Status
          <ArrowUpDown className="ml-1 h-3 w-3" />
        </Button>
      );
    },
    cell: ({ row }) => <StatusBadge status={row.getValue('status')}  />,
  },
  {
    id: 'eventLinks',
    header: 'Links',
    cell: ({ row }) => (
      <EventLinksCell
        eventID={row.original.eventID}
        clientID={row.original.clientID}
        clientName={row.original.clientName ?? undefined}
      />
    ),
    size: 70,
    enableSorting: false,
  },
];
